import { Component, DestroyRef, inject, output, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { InputLabel } from '../../../../parts/input-label/input-label.component';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { FinancialService } from '../../../../core/services/financial/financial.service';
import { ExpenseModel, UpdateExpenseRequest } from '../../../../core/models/data/expense.model';
import { BalancesMessagingConstants, GenericMessagingConstants } from '../../../../core/constants/messaging.constants';
import { BaseFormComponent } from '../../../../core/base-classes/base-form-component';
import { CanComponentDeactivate } from '../../../../core/routing/can-deactivate.guard';
import { DirtyFormRegistryService } from '../../../../core/services/dirty-form-registry.service';
import { EscCloseDirective } from '../../../../core/directives/esc-close.directive';
import { CurrencyDisplayPipe } from '../../../../core/pipes/currency/currency.pipe';
import { OrgDatePipe } from '../../../../core/pipes/org-date/org-date.pipe';
import { NoValuePipe } from '../../../../core/pipes/no-value/no-value.pipe';

/**
 * Edits the three fields an expense can change after posting: payee name, reference number and notes.
 * Amount, date paid, currency and the GL accounts are immutable — correcting one of those is a void
 * plus a re-entry — so they render read-only here for context. Validators mirror the create form
 * (forms/expense/expense.form.ts); keep the two in step.
 */
@Component({
  selector: 'ms-edit-expense-modal',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    InputLabel,
    ConfirmationModalComponent,
    InfoModalComponent,
    EscCloseDirective,
    CurrencyDisplayPipe,
    OrgDatePipe,
    NoValuePipe
  ],
  templateUrl: './edit-expense-modal.component.html'
})
export class EditExpenseModalComponent extends BaseFormComponent implements CanComponentDeactivate {

  //#region Properties & Signals

  public expenseUpdated = output<ExpenseModel>();

  public expense = signal<ExpenseModel | undefined>(undefined);

  public editForm: FormGroup;

  @ViewChild('editExpenseConfirmationRef') confirmationRef!: ConfirmationModalComponent;
  @ViewChild('editExpenseInfoRef') infoRef!: InfoModalComponent;
  @ViewChild('editExpenseEscConfirmationRef') escConfirmationRef!: ConfirmationModalComponent;

  //#endregion Properties & Signals

  //#region Constructor

  private dirtyFormRegistry = inject(DirtyFormRegistryService);
  private destroyRef = inject(DestroyRef);

  constructor(
    private fb: FormBuilder,
    private financialService: FinancialService,
    private bootstrapService: BootstrapService
  ) {
    super();

    this.destroyRef.onDestroy(() => this.dirtyFormRegistry.unregister(this));

    this.editForm = this.fb.group({
      vendorName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(250)]],
      referenceNumber: ['', [Validators.maxLength(30), Validators.pattern(/^[a-zA-Z0-9\-\/]*$/)]],
      description: ['', [Validators.maxLength(500)]]
    });
  }

  //#endregion Constructor

  //#region Public Methods

  public open(expense: ExpenseModel): void {
    this.expense.set(expense);
    this.submitSucceeded = false;

    this.editForm.reset({
      vendorName: expense.vendorName ?? '',
      referenceNumber: expense.referenceNumber ?? '',
      description: expense.description ?? ''
    });

    this.dirtyFormRegistry.register(this);
    this.bootstrapService.manageModal('#editExpenseModal', ModalActions.Show);
  }

  //#endregion Public Methods

  //#region Form Submission

  public onSubmit(): void {
    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      return;
    }

    this.confirmationRef.modalType.set(ModalTypes.Question);
    this.confirmationRef.message.set(BalancesMessagingConstants.EXPENSE_UPDATE_CONFIRMATION());
    this.confirmationRef.enableNotes.set(true);
    this.confirmationRef.isNoteRequired.set(false);

    this.bootstrapService.manageModal('#editExpenseModal', ModalActions.Hide);
    this.bootstrapService.manageModal('#editExpenseConfirmationModal', ModalActions.Show);
  }

  public async confirmSubmit(note?: string): Promise<void> {
    const expense = this.expense();
    if (!expense) { return; }

    this.isSubmitting = true;
    this.confirmationRef.isSubmitting.set(true);

    try {
      const result = await this.financialService.updateExpense(expense.expenseId, this.buildRequest(note));

      this.bootstrapService.manageModal('#editExpenseConfirmationModal', ModalActions.Hide);

      if (result.success && result.data) {
        this.submitSucceeded = true;
        this.dirtyFormRegistry.unregister(this);
        this.infoRef.modalType.set(ModalTypes.Success);
        this.infoRef.message.set(result.message || BalancesMessagingConstants.EXPENSE_UPDATE_SUCCESS());
        this.expenseUpdated.emit(result.data);
      } else {
        this.infoRef.modalType.set(ModalTypes.Failure);
        this.infoRef.message.set(result.message || BalancesMessagingConstants.EXPENSE_UPDATE_FAILURE());
      }

      this.bootstrapService.manageModal('#editExpenseInfoModal', ModalActions.Show);
    } catch {
      this.bootstrapService.manageModal('#editExpenseConfirmationModal', ModalActions.Hide);
      this.infoRef.modalType.set(ModalTypes.Failure);
      this.infoRef.message.set(BalancesMessagingConstants.EXPENSE_UPDATE_FAILURE());
      this.bootstrapService.manageModal('#editExpenseInfoModal', ModalActions.Show);
    } finally {
      this.isSubmitting = false;
      this.confirmationRef.isSubmitting.set(false);
    }
  }

  // Full desired state: an emptied optional field is sent as null, which clears it server-side.
  private buildRequest(note?: string): UpdateExpenseRequest {
    const formValue = this.editForm.value;

    return {
      vendorName: formValue.vendorName?.trim(),
      referenceNumber: formValue.referenceNumber?.trim() || null,
      description: formValue.description?.trim() || null,
      note: note?.trim() || null
    };
  }

  //#endregion Form Submission

  //#region Modal Management

  public confirmationCancelled(): void {
    this.bootstrapService.manageModal('#editExpenseConfirmationModal', ModalActions.Hide);
    this.bootstrapService.manageModal('#editExpenseModal', ModalActions.Show);
  }

  public onCancel(): void {
    if (this.editForm.dirty) {
      this.escConfirmationRef.modalType.set(ModalTypes.Question);
      this.escConfirmationRef.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
      this.bootstrapService.manageModal('#editExpenseModal', ModalActions.Hide);
      this.bootstrapService.manageModal('#editExpenseEscConfirmationModal', ModalActions.Show);
    } else {
      this.closeModal();
    }
  }

  public escConfirmed(): void {
    this.bootstrapService.manageModal('#editExpenseEscConfirmationModal', ModalActions.Hide);
    this.closeModal();
    this.resolvePendingLeave(true);
  }

  public escCancelled(): void {
    this.bootstrapService.manageModal('#editExpenseEscConfirmationModal', ModalActions.Hide);
    this.bootstrapService.manageModal('#editExpenseModal', ModalActions.Show);
    this.resolvePendingLeave(false);
  }

  public infoModalConfirmed(): void {
    this.bootstrapService.manageModal('#editExpenseInfoModal', ModalActions.Hide);

    if (this.submitSucceeded === false) {
      this.bootstrapService.manageModal('#editExpenseModal', ModalActions.Show);
    }
  }

  // Asked by canDeactivateGuard through DirtyFormRegistryService when navigation is attempted while
  // the modal is open. Returning a bare false would block the navigation with nothing on screen to
  // explain it, so the leave confirmation is shown and its outcome resolves the pending promise.
  public canDeactivate(): boolean | Promise<boolean> {
    return this.awaitLeaveConfirmation(this.editForm.dirty && !this.submitSucceeded, () => {
      this.escConfirmationRef.modalType.set(ModalTypes.Question);
      this.escConfirmationRef.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
      this.bootstrapService.manageModal('#editExpenseModal', ModalActions.Hide);
      this.bootstrapService.manageModal('#editExpenseEscConfirmationModal', ModalActions.Show);
    });
  }

  private closeModal(): void {
    this.bootstrapService.manageModal('#editExpenseModal', ModalActions.Hide);
    this.dirtyFormRegistry.unregister(this);
    this.editForm.reset();
  }

  //#endregion Modal Management
}
